import { useState } from 'react';

import { Grid } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateRangePicker, MultiInputDateRangeField } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useDebouncedCallback } from 'use-debounce';

import { StoreAutoComplete } from 'components/autocompletes';
import { CissTextField } from 'components/fields';
import { Filter } from 'components/filter';
import { FilterValuesProps } from 'hooks/useFilter';
import { RequestOptionsType } from 'hooks/useRequestListagem';
import { ConsultaReembolsoStatusAutoComplete } from 'pages/financeiro/reembolso/consulta-reembolso/components';
import { GerarReembolsoPromocaoAutoComplete } from 'pages/financeiro/reembolso/gerar-reembolso/components';

export function ConsultaReembolsoGridFilter() {
    const [filterLoja, setFilterLoja] = useState<FilterValuesProps>({
        idProperty: 'cdLoja',
        property: 'cdLojas',
        operator: 'in',
        value: [],
    });

    const [filterPromocoes, setFilterPromocoes] = useState<FilterValuesProps>({
        idProperty: 'idPromocao',
        property: 'idPromocoes',
        operator: 'in',
        value: [],
    });

    const [filterStatus, setFilterStatus] = useState<FilterValuesProps>({
        idProperty: 'cdApuracaoLojaNotaWrapperSituacao',
        property: 'cdApuracaoLojaNotaWrapperSituacao',
        operator: 'eq',
        value: null,
    });

    const [filterCupom, setFilterCupom] = useState<FilterValuesProps>({
        property: 'nrNota',
        operator: 'eq',
        value: null,
    });

    const [filterDtInicio, setFilterDtInicio] = useState<FilterValuesProps>({
        property: 'dtInicio',
        operator: 'eq',
        value: null,
        required: 'Data de início é obrigatória.',
    });

    const [filterDtFim, setFilterDtFim] = useState<FilterValuesProps>({
        property: 'dtFim',
        operator: 'eq',
        value: null,
        required: 'Data de fim é obrigatória.',
    });

    const isValidDates = isValid(filterDtInicio?.value) && isValid(filterDtFim?.value);

    let requestOptionsPromocao: RequestOptionsType | null = null;

    if (isValidDates) {
        requestOptionsPromocao = {
            url: '/financeiro/promocao/reembolso/periodo',
            sort: [{ property: 'idPromocao', direction: 'ASC' }],
            filter: [
                { property: 'dtInicio', operator: 'gte', value: format(filterDtInicio.value, 'MM/dd/yyyy') },
                { property: 'dtFim', operator: 'lte', value: format(filterDtFim.value, 'MM/dd/yyyy') },
            ],
        };
    }

    /**
     * Atualiza as datas de início e fim e reseta o filtro de promoções.
     */
    const updateDateFilters = (startDate: Date | null, endDate: Date | null) => {
        setFilterDtInicio((oldState: FilterValuesProps) => ({ ...oldState, value: startDate }));
        setFilterDtFim((oldState: FilterValuesProps) => ({ ...oldState, value: endDate }));
        setFilterPromocoes((oldState: FilterValuesProps) => ({ ...oldState, value: [] }));
    };

    /**
     * Atualiza filtros de data e promoções com debounce de 500 milissegundos.
     */
    const updateDateFiltersDebounced = useDebouncedCallback(updateDateFilters, 500);

    return (
        <Filter
            preventQueryParamsEmptyStart
            filters={[
                { value: filterLoja, setter: setFilterLoja },
                { value: filterCupom, setter: setFilterCupom },
                { value: filterDtInicio, setter: setFilterDtInicio },
                { value: filterDtFim, setter: setFilterDtFim },
                { value: filterPromocoes, setter: setFilterPromocoes },
                { value: filterStatus, setter: setFilterStatus },
            ]}
        >
            <Grid sx={{ width: '100%' }} size={{ lg: 6 }}>
                <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns} localeText={{ start: 'Data de início', end: 'Data de fim' }}>
                    <DateRangePicker
                        value={[filterDtInicio.value ?? null, filterDtFim.value ?? null]}
                        maxDate={new Date()}
                        onChange={([startDate, endDate], inputChange) => {
                            if (inputChange) {
                                updateDateFiltersDebounced(startDate, endDate);
                            } else {
                                updateDateFilters(startDate, endDate);
                            }
                        }}
                        slots={{ field: MultiInputDateRangeField }}
                        slotProps={{
                            field: { dateSeparator: 'até' },
                            textField: { autoComplete: 'off', size: 'small' },
                        }}
                    />
                </LocalizationProvider>
            </Grid>
            <Grid size={{ lg: 6 }}>
                <ConsultaReembolsoStatusAutoComplete
                    value={filterStatus.value}
                    onChange={(e, value) => setFilterStatus((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                />
            </Grid>
            <Grid size={{ lg: 4 }}>
                <GerarReembolsoPromocaoAutoComplete
                    multiple
                    size="small"
                    value={filterPromocoes.value}
                    disabled={!isValidDates}
                    onChange={(e, value) => setFilterPromocoes((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                    requestOptions={requestOptionsPromocao}
                />
            </Grid>
            <Grid size={{ lg: 4 }}>
                <StoreAutoComplete
                    multiple
                    size="small"
                    defaultStore={false}
                    importStores={false}
                    value={filterLoja.value}
                    onChange={(e, value) => setFilterLoja((oldState: FilterValuesProps) => ({ ...oldState, value }))}
                />
            </Grid>
            <Grid size={{ lg: 4 }}>
                <CissTextField
                    size="small"
                    label="Número nota/cupom"
                    value={filterCupom.value}
                    onChange={(e) => setFilterCupom((oldState: FilterValuesProps) => ({ ...oldState, value: e.target.value }))}
                />
            </Grid>
        </Filter>
    );
}
